import { useEffect } from 'react';
import { exportBoard, parseBoards } from '../util/boardTransfer.mjs';
import { readClipboard, readImages } from '../util/clipboard.js';

// Normalize browser paste events and clipboard-button reads.
const useNoteClipboard = function({ready, appendContent, onError, boardPosition, notes, selected, deleteNote, addNote}) {
  const pasteClipboard = async function(id) {
    const position = boardPosition();

    try {
      const content = await readClipboard();

      const hasImages = content.images.length > 0;

      // Images belong on the board even when a text note has focus.
      appendContent(hasImages ? null : id, hasImages ? {
        text: '',
        images: content.images
      } : content, position);
    } catch (error) {
      onError(error.message);
    }
  };

  const handlePaste = async function(event) {
    if (!ready || event.defaultPrevented) return;
    // Listen at document level so paste also works before the board has focus.
    const target = event.target instanceof Element ? event.target : document.body;

    if (!target.closest('#app') && target.matches('input, textarea, [contenteditable]')) return;

    const serialized = event.clipboardData.getData('application/x-stickii');
    if (serialized && !target.closest('input, textarea, [contenteditable]')) {
      event.preventDefault();
      try {
        const note = parseBoards(serialized)[0].notes[0];
        if (note) addNote({...note, ...(boardPosition() || {x: note.x + 20, y: note.y + 20})});
      } catch (error) {
        onError(error.message);
      }
      return;
    }

    const id = target.closest('[data-note-id]')?.dataset.noteId;
    const imageItems = [...event.clipboardData.items].filter(item => item.kind === 'file' && item.type.startsWith('image/')).map(item => item.getAsFile()).filter(Boolean);
    const files = imageItems.length ? imageItems : [...event.clipboardData.files].filter(file => file.type.startsWith('image/'));
    const text = event.clipboardData.getData('text/plain');

    // Native textarea pasting preserves the caret, selection, and undo behavior.
    if (!files.length && target.matches('textarea, input, [contenteditable]')) return;

    if (!files.length && !text) return;

    event.preventDefault();

    const position = boardPosition();

    try {
      // Image pastes always create standalone board images, regardless of focus.
      const images = await readImages(files);

      appendContent(images.length ? null : id || null, {
        text: images.length ? '' : text,
        images
      }, position);
    } catch (error) {
      onError(error.message);
    }
  };

  const handleCopy = function(event) {
    if (!ready || event.defaultPrevented || !event.clipboardData) return;
    const target = event.target instanceof Element ? event.target : document.body;
    if (target.closest('input, textarea, select, [contenteditable]') || window.getSelection()?.toString()) return;
    const note = notes.find(item => item.id === selected);
    if (!note) return;
    try {
      event.clipboardData.setData('application/x-stickii', exportBoard('clipboard', [note]));
      event.clipboardData.setData('text/plain', note.text || note.title || note.images[0]?.name || '');
      event.preventDefault();
      if (event.type === 'cut') deleteNote(note.id);
    } catch (error) {
      onError(error.message);
    }
  };

  useEffect(() => {
    document.addEventListener('paste', handlePaste);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('cut', handleCopy);
    return () => {
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('cut', handleCopy);
    };
  }, [handlePaste, handleCopy]);

  return {
    pasteClipboard
  };
};

export default useNoteClipboard;
